import React, { useEffect, useState } from 'react';
import {
  Plus, Star, Copy, Archive, ArchiveRestore, Trash2, Pin, PlayCircle,
  History, Loader2, LayoutTemplate,
} from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { SkeletonRows, EmptyState } from './OrgPanelShared';

/** Enterprise Shift Templates (shared employer + agency): create, edit,
 * duplicate, favorite, default, archive/restore, version history, and
 * generate single/multiple/recurring/bulk shifts wired into the
 * assignment engine (roster notify + smart-match preview). */

const FORM_FIELDS = [
  { key: 'name', label: 'Template name *', type: 'text' },
  { key: 'category', label: 'Category', type: 'text' },
  { key: 'shift_type', label: 'Shift type', type: 'text' },
  { key: 'position', label: 'Position', type: 'text' },
  { key: 'department', label: 'Department', type: 'text' },
  { key: 'program', label: 'Program / Facility', type: 'text' },
  { key: 'address', label: 'Location address', type: 'text' },
  { key: 'start_time_of_day', label: 'Start time (HH:MM)', type: 'text' },
  { key: 'shift_duration_hours', label: 'Duration (hours)', type: 'number' },
  { key: 'workers_needed', label: 'Workers needed', type: 'number' },
  { key: 'pay_rate', label: 'Pay rate ($/hr)', type: 'number' },
  { key: 'billing_rate', label: 'Billing rate ($/hr)', type: 'number' },
  { key: 'required_skills', label: 'Required skills (comma-separated)', type: 'text' },
  { key: 'required_certifications', label: 'Required certifications (comma-separated)', type: 'text' },
  { key: 'notes', label: 'Notes', type: 'textarea' },
  { key: 'internal_instructions', label: 'Internal instructions', type: 'textarea' },
];

function tplToForm(t) {
  return {
    ...t,
    address: t.location?.address || '',
    required_skills: (t.required_skills || []).join(', '),
    required_certifications: (t.required_certifications || []).join(', '),
  };
}

function formToPayload(f) {
  const p = { ...f };
  p.location = { address: f.address || '' };
  delete p.address;
  ['shift_duration_hours', 'workers_needed', 'pay_rate', 'billing_rate'].forEach((k) => {
    if (p[k] !== undefined && p[k] !== '') p[k] = Number(p[k]);
  });
  return p;
}

export default function ShiftTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // form values or null
  const [generating, setGenerating] = useState(null); // template or null
  const [historyFor, setHistoryFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall(`/api/org/shift-templates?include_archived=${showArchived}`);
      setTemplates(res.templates || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load();   }, [showArchived]);

  const act = async (path, msg) => {
    try { await apiCall(path, { method: 'POST' }); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(e.message); }
  };

  const remove = async (tid) => {
    if (!window.confirm('Delete this template permanently?')) return;
    try { await apiCall(`/api/org/shift-templates/${tid}`, { method: 'DELETE' }); toast.success('Template deleted'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4" data-testid="shift-templates-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <LayoutTemplate className="w-4 h-4 text-indigo-600" /> Shift Templates
        </h3>
        <div className="flex items-center gap-3">
          <label className="text-xs text-slate-600 flex items-center gap-1.5">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="templates-show-archived" />
            Show archived
          </label>
          <button onClick={() => setEditing({ start_time_of_day: '09:00', shift_duration_hours: 8, workers_needed: 1 })}
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            data-testid="template-create-btn">
            <Plus className="w-4 h-4" /> New template
          </button>
        </div>
      </div>

      {loading ? <SkeletonRows /> : templates.length === 0 ? (
        <EmptyState label="No shift templates yet. Create one to schedule repeat shifts in seconds." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((t) => (
            <div key={t.template_id}
              className={`bg-white border rounded-xl p-4 space-y-2 ${t.archived ? 'border-dashed border-slate-300 opacity-70' : 'border-slate-200'}`}
              data-testid={`template-card-${t.template_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                    {t.name || t.title}
                    {t.is_default && <span className="px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[9px] font-bold uppercase">Default</span>}
                    {t.archived && <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[9px] font-bold uppercase">Archived</span>}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {[t.category, t.position, t.department].filter(Boolean).join(' · ') || 'General'}
                    {' · '}{t.start_time_of_day || '09:00'} · {t.shift_duration_hours || 8}h · ${t.pay_rate || 0}/hr · v{t.version || 1}
                  </p>
                </div>
                <button onClick={() => act(`/api/org/shift-templates/${t.template_id}/favorite`)}
                  className={`p-1.5 rounded-lg ${t.favorite ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'}`}
                  title="Favorite" data-testid={`template-fav-${t.template_id}`}>
                  <Star className="w-4 h-4" fill={t.favorite ? 'currentColor' : 'none'} />
                </button>
              </div>
              <div className="flex items-center gap-1 flex-wrap pt-1">
                {!t.archived && (
                  <button onClick={() => setGenerating(t)}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                    data-testid={`template-generate-${t.template_id}`}>
                    <PlayCircle className="w-3.5 h-3.5" /> Generate shifts
                  </button>
                )}
                <button onClick={() => setEditing(tplToForm(t))} className="text-xs px-2.5 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
                  data-testid={`template-edit-${t.template_id}`}>Edit</button>
                <button onClick={() => act(`/api/org/shift-templates/${t.template_id}/duplicate`, 'Duplicated')}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" title="Duplicate"
                  data-testid={`template-duplicate-${t.template_id}`}><Copy className="w-4 h-4" /></button>
                {!t.is_default && !t.archived && (
                  <button onClick={() => act(`/api/org/shift-templates/${t.template_id}/default`, 'Set as default')}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" title="Set default"
                    data-testid={`template-default-${t.template_id}`}><Pin className="w-4 h-4" /></button>
                )}
                {(t.history || []).length > 0 && (
                  <button onClick={() => setHistoryFor(t)} className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" title="Version history"
                    data-testid={`template-history-${t.template_id}`}><History className="w-4 h-4" /></button>
                )}
                {t.archived ? (
                  <button onClick={() => act(`/api/org/shift-templates/${t.template_id}/restore`, 'Restored')}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50" title="Restore"
                    data-testid={`template-restore-${t.template_id}`}><ArchiveRestore className="w-4 h-4" /></button>
                ) : (
                  <button onClick={() => act(`/api/org/shift-templates/${t.template_id}/archive`, 'Archived')}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50" title="Archive"
                    data-testid={`template-archive-${t.template_id}`}><Archive className="w-4 h-4" /></button>
                )}
                <button onClick={() => remove(t.template_id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" title="Delete"
                  data-testid={`template-delete-${t.template_id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <TemplateFormModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {generating && <GenerateModal template={generating} onClose={() => setGenerating(null)} />}
      {historyFor && <HistoryModal template={historyFor} onClose={() => setHistoryFor(null)} onRestored={() => { setHistoryFor(null); load(); }} />}
    </div>
  );
}

function TemplateFormModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial.template_id);

  const save = async () => {
    if (!String(form.name || '').trim()) { toast.error('Template name is required'); return; }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (isEdit) {
        await apiCall(`/api/org/shift-templates/${initial.template_id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('Template updated (new version saved)');
      } else {
        await apiCall('/api/org/shift-templates', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('Template created');
      }
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="template-form-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl p-5 max-h-[85vh] overflow-y-auto">
        <h3 className="font-bold text-slate-900 mb-3">{isEdit ? 'Edit template' : 'New shift template'}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FORM_FIELDS.map((f) => (
            <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
              <label className="text-xs font-medium text-slate-600">{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea value={form[f.key] || ''} onChange={(e) => setForm((v) => ({ ...v, [f.key]: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={2}
                  data-testid={`template-field-${f.key}`} />
              ) : (
                <input type={f.type} value={form[f.key] ?? ''} onChange={(e) => setForm((v) => ({ ...v, [f.key]: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid={`template-field-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-4">
          <button onClick={save} disabled={saving}
            className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            data-testid="template-save-btn">
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
          </button>
          <button onClick={onClose} className="flex-1 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
            data-testid="template-cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function GenerateModal({ template, onClose }) {
  const today = new Date().toISOString().split('T')[0];
  const [mode, setMode] = useState('single');
  const [startDate, setStartDate] = useState(today);
  const [dates, setDates] = useState('');
  const [pattern, setPattern] = useState('weekly');
  const [occurrences, setOccurrences] = useState(4);
  const [intervalDays, setIntervalDays] = useState(3);
  const [days, setDays] = useState(5);
  const [asDraft, setAsDraft] = useState(false);
  const [autoMatch, setAutoMatch] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const body = { mode, start_date: startDate, as_draft: asDraft, auto_match: autoMatch };
      if (mode === 'multiple') body.dates = dates.split(',').map((d) => d.trim()).filter(Boolean);
      if (mode === 'recurring') { body.pattern = pattern; body.occurrences = Number(occurrences); if (pattern === 'custom') body.interval_days = Number(intervalDays); }
      if (mode === 'bulk') body.days = Number(days);
      const res = await apiCall(`/api/org/shift-templates/${template.template_id}/generate`, { method: 'POST', body: JSON.stringify(body) });
      setResult(res);
      toast.success(`${res.created} shift${res.created === 1 ? '' : 's'} generated${res.roster_notified ? ` — ${res.roster_notified} roster workers notified` : ''}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="template-generate-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-3 max-h-[85vh] overflow-y-auto">
        <h3 className="font-bold text-slate-900">Generate shifts — {template.name || template.title}</h3>
        <div>
          <label className="text-xs font-medium text-slate-600">Mode</label>
          <select value={mode} onChange={(e) => setMode(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" data-testid="generate-mode-select">
            <option value="single">Single shift</option>
            <option value="multiple">Multiple dates</option>
            <option value="recurring">Recurring series</option>
            <option value="bulk">Bulk (consecutive days)</option>
          </select>
        </div>
        {mode !== 'multiple' && (
          <div>
            <label className="text-xs font-medium text-slate-600">Start date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="generate-start-date" />
          </div>
        )}
        {mode === 'multiple' && (
          <div>
            <label className="text-xs font-medium text-slate-600">Dates (comma-separated YYYY-MM-DD)</label>
            <input value={dates} onChange={(e) => setDates(e.target.value)} placeholder="2026-08-10, 2026-08-14"
              className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="generate-dates" />
          </div>
        )}
        {mode === 'recurring' && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-slate-600">Pattern</label>
              <select value={pattern} onChange={(e) => setPattern(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" data-testid="generate-pattern">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Every N days</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Occurrences</label>
              <input type="number" min={1} max={30} value={occurrences} onChange={(e) => setOccurrences(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="generate-occurrences" />
            </div>
            {pattern === 'custom' && (
              <div>
                <label className="text-xs font-medium text-slate-600">Interval (days)</label>
                <input type="number" min={1} max={90} value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="generate-interval" />
              </div>
            )}
          </div>
        )}
        {mode === 'bulk' && (
          <div>
            <label className="text-xs font-medium text-slate-600">Consecutive days</label>
            <input type="number" min={1} max={30} value={days} onChange={(e) => setDays(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="generate-days" />
          </div>
        )}
        <div className="flex items-center gap-4 text-xs text-slate-600">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={asDraft} onChange={(e) => setAsDraft(e.target.checked)} data-testid="generate-as-draft" />
            Create as drafts
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={autoMatch} onChange={(e) => setAutoMatch(e.target.checked)} data-testid="generate-auto-match" />
            Smart-match preview
          </label>
        </div>
        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800 space-y-1" data-testid="generate-result">
            <p className="font-semibold">{result.created} shift(s) created ({result.status}){result.roster_notified ? ` · ${result.roster_notified} roster notified` : ''}</p>
            {(result.top_matches || []).length > 0 && (
              <div>
                <p className="font-medium mt-1">Top matched workers:</p>
                {result.top_matches.slice(0, 5).map((m, i) => (
                  <p key={i}>{i + 1}. {m.name || m.worker_name || m.email} — score {Math.round(m.score || 0)}</p>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex gap-2 pt-1">
          <button onClick={generate} disabled={busy}
            className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
            data-testid="generate-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            {busy ? 'Generating…' : 'Generate'}
          </button>
          <button onClick={onClose} className="flex-1 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
            data-testid="generate-close-btn">{result ? 'Done' : 'Cancel'}</button>
        </div>
      </div>
    </div>
  );
}

function HistoryModal({ template, onClose, onRestored }) {
  const restore = async (version) => {
    try {
      await apiCall(`/api/org/shift-templates/${template.template_id}/restore-version`, {
        method: 'POST', body: JSON.stringify({ version }),
      });
      toast.success(`Version ${version} restored`);
      onRestored();
    } catch (e) { toast.error(e.message); }
  };
  const history = [...(template.history || [])].reverse();
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="template-history-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-3 max-h-[80vh] overflow-y-auto">
        <h3 className="font-bold text-slate-900">Version history — {template.name || template.title}</h3>
        <p className="text-xs text-slate-500">Current version: v{template.version || 1}</p>
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
          {history.map((h, i) => (
            <li key={i} className="flex items-center justify-between px-4 py-2.5">
              <div>
                <p className="text-sm font-medium text-slate-900">v{h.version}</p>
                <p className="text-[10px] text-slate-400">{h.saved_at ? new Date(h.saved_at).toLocaleString() : ''}</p>
              </div>
              <button onClick={() => restore(h.version)} className="text-xs px-2.5 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
                data-testid={`history-restore-${h.version}`}>Restore</button>
            </li>
          ))}
        </ul>
        <button onClick={onClose} className="w-full py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200">Close</button>
      </div>
    </div>
  );
}
